import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { Mail, Lock, Check, ArrowRight } from 'lucide-react';
import authService, { LoginDto } from '../services/auth.service';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginDto>({
    email: '',
    motdepasse: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<LoginDto>>({});
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved credentials if "remember me" was checked
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    const savedRememberMe = localStorage.getItem('remember_me') === 'true';

    if (savedEmail && savedRememberMe) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const validate = (): boolean => {
    const newErrors: Partial<LoginDto> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.motdepasse) {
      newErrors.motdepasse = 'Mot de passe requis';
    } else if (formData.motdepasse.length < 6) {
      newErrors.motdepasse = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await authService.login(formData);

      // Handle "remember me" functionality
      if (rememberMe) {
        localStorage.setItem('remembered_email', formData.email);
        localStorage.setItem('remember_me', 'true');
      } else {
        localStorage.removeItem('remembered_email');
        localStorage.removeItem('remember_me');
      }

      // Validate token after login
      const token = authService.getToken();

      if (token && authService.isTokenValid(token)) {
        toast.success('Connexion réussie!');

        // Get user profile and redirect based on role
        const user = authService.getUserProfile();

        if (user?.roles && user.roles.length > 0) {
          // Redirect to admin pages if user has admin role
          const roleName = user.roles[0]?.toLowerCase();
          if (roleName === 'admin' || roleName.includes('admin')) {
            navigate('/admin/dashboard');
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      } else {
        throw new Error('Invalid token received');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Échec de la connexion. Veuillez vérifier vos identifiants.';
      toast.error(errorMessage);
      setErrors({ motdepasse: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof LoginDto]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-white overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary-50 to-transparent opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-t from-primary-50 to-transparent opacity-40"></div>
        {/* Abstract decorative shapes - very subtle */}
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 rounded-full bg-primary/5 blur-3xl"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-72 h-72 rounded-full bg-secondary/5 blur-3xl"></div>
      </div>

      {/* Main Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md px-4 sm:px-6"
      >
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-12 backdrop-blur-sm">

          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-block mb-6 relative"
            >
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-50"></div>
              <img
                src="/SAPI_favicon_icon_128.png"
                alt="SAPI Logo"
                className="w-20 h-20 relative z-10 drop-shadow-md"
              />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-3">
              Espace Administrateur
            </h2>
            <p className="text-gray-500 font-medium text-sm sm:text-base">
              Connectez-vous pour gérer la plateforme SAPI
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 ml-1">
                Email
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full pl-11 pr-4 py-4 border-0 ring-1 ring-inset ${errors.email
                    ? 'ring-red-300 focus:ring-red-500 bg-red-50/50'
                    : 'ring-gray-100 focus:ring-primary bg-white shadow-sm'
                    } rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-gray-900 placeholder-gray-400 sm:text-sm font-medium`}
                  placeholder="votre@email.com"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1 ml-1 font-medium">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="motdepasse" className="block text-sm font-semibold text-gray-700 ml-1">
                  Mot de passe
                </label>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                </div>
                <input
                  id="motdepasse"
                  name="motdepasse"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.motdepasse}
                  onChange={handleChange}
                  className={`block w-full pl-11 pr-4 py-4 border-0 ring-1 ring-inset ${errors.motdepasse
                    ? 'ring-red-300 focus:ring-red-500 bg-red-50/50'
                    : 'ring-gray-100 focus:ring-primary bg-white shadow-sm'
                    } rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-gray-900 placeholder-gray-400 sm:text-sm font-medium`}
                  placeholder="••••••••"
                />
              </div>
              {errors.motdepasse && (
                <p className="text-xs text-red-500 mt-1 ml-1 font-medium">
                  {errors.motdepasse}
                </p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer transition-colors"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600 cursor-pointer font-medium">
                  Se souvenir de moi
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-primary hover:text-primary-dark transition-colors">
                  Mot de passe oublié?
                </a>
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.99 }}
              className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-xl text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all duration-200 ${isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-dark hover:shadow-primary/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                }`}
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <div className="flex items-center">
                  Se connecter <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              )}
            </motion.button>
          </form>

          {/* Footer */}
          <div className="mt-10 text-center text-xs font-medium text-gray-400">
            &copy; {new Date().getFullYear()} SAPI. Tous droits réservés.
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
